// ADSR envelope.
//
// Two halves that mirror each other:
//   - Pure math (`envelopeValueAt`, `stageTimes`) — no AudioContext, unit-tested.
//   - A scheduler (`ADSR`) that writes the same shape onto a real AudioParam,
//     using cancelAndHoldAtTime so a re-trigger before release is click-free
//     (it ramps from the value held *now*, not from zero).

export interface ADSRValues {
  /** seconds */
  attack: number;
  /** seconds */
  decay: number;
  /** 0..1 sustain level (fraction of peak) */
  sustain: number;
  /** seconds */
  release: number;
}

export const DEFAULT_ADSR: ADSRValues = {
  attack: 0.005,
  decay: 0.2,
  sustain: 0.8,
  release: 0.3,
};

/** Absolute times of the attack/decay stage boundaries given a note-on time. */
export function stageTimes(v: ADSRValues, onTime: number) {
  const attackEnd = onTime + Math.max(0, v.attack);
  const decayEnd = attackEnd + Math.max(0, v.decay);
  return { attackEnd, decayEnd };
}

/**
 * Envelope value at absolute time `t`, for a note that turned on at `onTime`
 * and (optionally) released at `releaseTime`. Linear attack/decay/release.
 * `peak` scales the whole shape. Used by tests and as the reference the
 * scheduler mirrors.
 */
export function envelopeValueAt(
  v: ADSRValues,
  t: number,
  onTime: number,
  releaseTime: number | null,
  peak = 1,
): number {
  if (t <= onTime) return 0;
  const { attackEnd, decayEnd } = stageTimes(v, onTime);
  const sustainLevel = peak * clamp01(v.sustain);

  // Level the env had reached at the moment of release (held value to ramp from).
  const heldAt = (time: number): number => {
    if (time <= attackEnd) {
      return v.attack <= 0 ? peak : peak * ((time - onTime) / v.attack);
    }
    if (time <= decayEnd) {
      if (v.decay <= 0) return sustainLevel;
      const frac = (time - attackEnd) / v.decay;
      return peak + (sustainLevel - peak) * frac;
    }
    return sustainLevel;
  };

  if (releaseTime !== null && t >= releaseTime) {
    const start = heldAt(releaseTime);
    if (v.release <= 0) return 0;
    const frac = (t - releaseTime) / v.release;
    return frac >= 1 ? 0 : start * (1 - frac);
  }

  return heldAt(t);
}

function clamp01(x: number): number {
  return Math.min(1, Math.max(0, x));
}

export class ADSR {
  private values: ADSRValues;

  constructor(values: Partial<ADSRValues> = {}) {
    this.values = { ...DEFAULT_ADSR, ...values };
  }

  set(partial: Partial<ADSRValues>): void {
    this.values = { ...this.values, ...partial };
  }

  get(): ADSRValues {
    return { ...this.values };
  }

  /** Schedule attack -> decay -> sustain on `param`, starting at `when`. */
  trigger(param: AudioParam, when: number, peak = 1): void {
    const { attack, decay, sustain } = this.values;
    const sustainLevel = peak * clamp01(sustain);

    holdNow(param, when);
    param.linearRampToValueAtTime(peak, when + Math.max(MIN_T, attack));
    param.linearRampToValueAtTime(
      sustainLevel,
      when + Math.max(MIN_T, attack) + Math.max(MIN_T, decay),
    );
  }

  /** Schedule the release ramp to 0 starting at `when`. */
  release(param: AudioParam, when: number): void {
    const { release } = this.values;
    holdNow(param, when);
    param.linearRampToValueAtTime(0, when + Math.max(MIN_T, release));
  }
}

// A tiny floor so ramps never collapse to a zero-length (which Web Audio treats
// as an instantaneous jump and can click).
const MIN_T = 0.001;

/** Hold whatever the param's value is at `when`, so the next ramp starts there. */
function holdNow(param: AudioParam, when: number): void {
  if (typeof param.cancelAndHoldAtTime === 'function') {
    param.cancelAndHoldAtTime(when);
  } else {
    // Fallback for older engines.
    const v = param.value;
    param.cancelScheduledValues(when);
    param.setValueAtTime(v, when);
  }
}
